import { useState } from 'react';
import type { ItineraryService, ServiceType } from '../../../../mocks/itinerary';
import { serviceTypeConfig } from '../../../../mocks/itinerary';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;
const SERVICE_TYPES: ServiceType[] = ['Hotel', 'Flight', 'Tour', 'Activity', 'Transfer', 'Meal', 'Cruise', 'Visa', 'Other'];

interface Props {
  dayNumber: number;
  editService?: ItineraryService | null;
  onSave: (service: ItineraryService) => void;
  onClose: () => void;
}

export default function AddServiceModal({ dayNumber, editService, onSave, onClose }: Props) {
  const [type, setType] = useState<ServiceType>(editService?.type ?? 'Tour');
  const [name, setName] = useState(editService?.name ?? '');
  const [time, setTime] = useState(editService?.time ?? '');
  const [description, setDescription] = useState(editService?.description ?? '');
  const [costPrice, setCostPrice] = useState<string>(editService ? String(editService.costPrice) : '');
  const [sellPrice, setSellPrice] = useState<string>(editService ? String(editService.sellPrice) : '');
  const [notes, setNotes] = useState(editService?.notes ?? '');

  const cost = parseFloat(costPrice) || 0;
  const sell = parseFloat(sellPrice) || 0;
  const margin = sell > 0 ? Math.round(((sell - cost) / sell) * 100) : 0;
  const profit = sell - cost;

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      id: editService?.id ?? `s-${Date.now()}`,
      type,
      name: name.trim(),
      time,
      description,
      costPrice: cost,
      sellPrice: sell,
      notes,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-800 text-base">
              {editService ? 'Edit Service' : 'Add Service'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Day {dayNumber}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Service Type */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Service Type</label>
            <div className="grid grid-cols-3 gap-2">
              {SERVICE_TYPES.map((t) => {
                const cfg = serviceTypeConfig[t];
                return (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl border text-xs font-medium transition-all cursor-pointer"
                    style={type === t ? { borderColor: brand, backgroundColor: `${brand}10`, color: brand } : { borderColor: '#E5E7EB', color: '#6B7280' }}
                  >
                    <span
                      className="w-7 h-7 flex items-center justify-center rounded-lg"
                      style={type === t ? { backgroundColor: brand, color: '#fff' } : { backgroundColor: cfg.bg, color: cfg.color }}
                    >
                      <i className={`${cfg.icon} text-sm`} />
                    </span>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Service Name <span className="text-red-400">*</span></label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-gray-400"
              placeholder="e.g. Emirates EK649, Soneva Fushi Check-in..."
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Time + Description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Time (optional)</label>
              <input
                type="time"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-gray-400"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Quick Note (optional)</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-gray-400"
                placeholder="Brief note..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Description (shown in PDF)</label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-gray-400 resize-none"
              placeholder="Describe this service as it will appear in the itinerary PDF..."
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Pricing */}
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <p className="text-xs font-semibold text-gray-600 mb-3">Pricing</p>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cost Price (AED)</label>
                <input
                  type="number"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-gray-400"
                  placeholder="0"
                  value={costPrice}
                  onChange={(e) => setCostPrice(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Sell Price (AED)</label>
                <input
                  type="number"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-gray-400"
                  placeholder="0"
                  value={sellPrice}
                  onChange={(e) => setSellPrice(e.target.value)}
                />
              </div>
            </div>
            {sell > 0 && (
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-white rounded-lg px-3 py-2 text-center border border-gray-100">
                  <p className="text-xs text-gray-400">Profit</p>
                  <p className={`text-sm font-semibold ${profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    AED {profit.toLocaleString()}
                  </p>
                </div>
                <div className="flex-1 bg-white rounded-lg px-3 py-2 text-center border border-gray-100">
                  <p className="text-xs text-gray-400">Margin</p>
                  <p className={`text-sm font-semibold ${margin >= 15 ? 'text-emerald-600' : margin >= 5 ? 'text-amber-500' : 'text-red-500'}`}>
                    {margin}%
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap disabled:opacity-40"
            style={{ backgroundColor: brand }}
          >
            {editService ? 'Save Changes' : 'Add Service'}
          </button>
        </div>
      </div>
    </div>
  );
}
